import { FieldValues, useFormState, UseFormReturn, useWatch } from 'react-hook-form';

/**
 * Tracks whether a form has unsaved changes vs. the values it was loaded with.
 *
 * The form's own dirtyFields decides which fields are worth looking at. Those candidates are
 * then re-checked with a comparison that treats two cases as unchanged which the form's own
 * comparison does not:
 *
 *   1. Empty values. "Field never set" (null / undefined) and "field cleared by the user"
 *      ('') are different to the form but identical to the user, so clearing a field that
 *      arrived empty should not count as a change.
 *   2. Dates. Values loaded from the API are ISO strings with a UTC time component, while
 *      the date picker produces Date objects at local midnight. Same day to the user,
 *      different values to the form — so a "pick the original date back" revert would
 *      otherwise never undirty the form. react-hook-form says as much itself: isDirty
 *      "does not support custom objects, classes, or File objects".
 *
 * Both rules apply at every depth, because dirtyFields is only a rough filter. Any field-array
 * operation makes react-hook-form rebuild dirtyFields from a whole-form walk that leaves a
 * container node for every object and array, so after one append every top-level key is a
 * candidate, untouched ones included. Comparing those by JSON alone would report any form
 * holding a nested date as dirty forever.
 *
 * TODO (DEVF-145): once dates are entered and stored in one canonical form app-wide, the date
 * case disappears and this can read isDirty straight off the form. That work is not currently
 * prioritised, so this stands until it is.
 */
export function useFormDirtyTracking<T extends FieldValues>(formApi: UseFormReturn<T>): boolean {
    const { dirtyFields, defaultValues } = useFormState({ control: formApi.control });
    const values = useWatch({ control: formApi.control });

    return Object.keys(dirtyFields).some((name) => toComparableValue(values?.[name]) !== toComparableValue(defaultValues?.[name]));
}

/**
 * Reduces a value to a string that compares equal whenever the user would call it unchanged.
 * Exported because the DUA Details tab keeps its fields in React state rather than in a form,
 * so it has to make the same comparison with no form to read it from.
 */
export function toComparableValue(value: unknown): string {
    if (value === undefined || value === null || value === '') {
        return '';
    }

    // Reduce dates to the calendar day so the time and timezone carried by the two
    // representations don't register as a change.
    if (value instanceof Date) {
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return value.substring(0, 10);
    }

    //positions are kept, so an append, a removal or a reorder always reads as a change.
    if (Array.isArray(value)) {
        return `[${value.map(toComparableValue).join(',')}]`;
    }

    //keys are sorted so key order alone is not a change, and keys that reduce to empty are
    //dropped so "key absent" and "key present but empty" agree, as rule 1 does for scalars.
    if (isPlainObject(value)) {
        const entries = Object.keys(value)
            .sort()
            .map((key) => [key, toComparableValue(value[key])] as const)
            .filter(([, comparable]) => comparable !== '')
            .map(([key, comparable]) => `${key}:${comparable}`);

        return `{${entries.join(',')}}`;
    }

    return JSON.stringify(value) ?? '';
}

//anything that isn't a plain object - a File, a class instance - is left to JSON.stringify,
//which is how everything but a Date was handled before this comparison started recursing.
function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;
}
